import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setStatus("loading");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, source: "homepage" });
      setStatus("done");
      toast.success(data.already_subscribed ? "You're already on the list!" : "You're in — thanks!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed — try again");
      setStatus("idle");
    }
  };

  return (
    <section
      className="relative overflow-hidden bg-halo-red text-white py-16 px-6 md:px-12 lg:px-16"
      data-testid="newsletter-signup"
    >
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #fff 1px, transparent 1px)", backgroundSize: "4px 4px" }} />
      <div className="max-w-4xl mx-auto relative text-center">
        <Mail className="w-10 h-10 mx-auto mb-4" strokeWidth={2} />
        <div className="font-sports uppercase tracking-[0.3em] text-xs mb-3 opacity-90">Never Miss An Episode</div>
        <h2 className="font-sports uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">
          Join the <span className="text-halo-navy">Section.</span>
        </h2>
        <p className="mt-5 font-body max-w-lg mx-auto opacity-90 text-base sm:text-lg">
          Get postgame reactions, live-show alerts, and exclusive fan drops in your inbox. No spam, ever.
        </p>

        {status === "done" ? (
          <div className="mt-8 inline-flex items-center gap-3 bg-white text-halo-navy px-6 py-4 font-sports uppercase tracking-wider text-sm" data-testid="newsletter-done">
            <CheckCircle2 className="w-5 h-5 text-halo-red" strokeWidth={2.5} />
            You're in — welcome to the Section
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === "loading"}
              className="flex-1 px-5 py-4 border-2 border-white/40 bg-white/10 backdrop-blur text-white placeholder-white/60 focus:outline-none focus:border-white font-body"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="newsletter-submit-btn"
              className="bg-halo-navy hover:bg-halo-navyLight text-white font-sports uppercase tracking-wider px-7 py-4 text-sm disabled:opacity-50 whitespace-nowrap"
            >
              {status === "loading" ? "…" : "Sign Me Up"}
            </button>
          </form>
        )}

        <p className="mt-4 text-[11px] font-body opacity-70">
          By subscribing you agree to receive occasional emails from Halos in the Infield. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
}
